import { keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import i18n from 'i18next';
import {
  type IObservableArray,
  makeAutoObservable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';

import type {
  CartBundlesCalculationItemRequest,
  CartCalculationItemRequest,
  CartCalculationItemResponse,
  CartCalculationOrderResponse,
  CartCalculationRequest,
  CartCalculationResponse,
  PickupSlotsPolicy,
} from '~/api/Catalog';
import { CatalogRequests, ProductCommodityGroupAPI } from '~/api/Catalog';
import { DeliveryMechanic, DeliveryMethods } from '~/api/ETA';
import { adaptV3CalculateToV2, detectV3BlockingIssue } from '~/api/v3/adapters';
import { V3Requests } from '~/api/v3/requests';
import type {
  V3AvailablePup,
  V3CalculateChildOrder,
  V3CalculateErrorBody,
  V3CalculateRequest,
  V3CalculateResponse,
  V3Destination,
  V3Issue,
  V3OrderBundleInput,
  V3OrderItemInput,
  V3RouteSelectionItem,
  V3Totals,
} from '~/api/v3/types';
import { company } from '~/company/Company';
import { getProductLimit } from '~/hooks/useProductLimit';
import { isItemAgeRestricted } from '~/services/AgeRestriction';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import type { AddToCartSource } from '~/services/analytics/sources';
import {
  DOMAIN,
  REASON,
  reportError,
  reportWarning,
} from '~/services/telemetry';
import { settingStore, userStore } from '~/stores';
import type { Offer, Product } from '~/stores/CategoriesStore';
import type {
  CalculateStockBundleError,
  CalculateStockErrorInstance,
  CalculateStockOfferError,
} from '~/stores/shared/CartStockChanges/interfaces';
import { calcProductOfferFields, isProductBundle } from '~/types/Product';
import { splitAddressLine } from '~/utils/addressLine';
import {
  countMixAndMatchUnits,
  getMixAndMatchProgress,
  type MixAndMatchProgress,
} from '~/utils/mixAndMatch';
import {
  getApplicableMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';
import { capByOrderLimit } from '~/utils/orderLimit';
import { isPickupPointErrorCode } from '~/utils/pickupPointErrors';

import type { CheckoutStore } from '../CheckoutStore/CheckoutStore';
import { pickupRejectionCode } from '../CheckoutStore/pickupSlots';
import { PROMOCODE_ERROR_CODE } from '../constants';
import { storage as localStorage } from '../LocalStorage';
import type { MainStore } from '../MainStore';
import type { OrderStore } from '../OrderStore/OrderStore';
import { PromotionStore } from '../PromotionsStore';
import { CartStockChanges } from '../shared/CartStockChanges';
import { Query } from '../shared/Query';
import type { UserStore } from '../UserStore';
import type {
  CalculationProcess,
  CartItem,
  CartItemBundle,
  CartItemOffer,
  CartMembershipMarkerSource,
  FirebaseCartItem,
  ICartStore,
  Promocode,
  TotalCartPrice,
} from './interfaces';
import { calculateDepsKey, cartQueryKeys } from './queryKeys';
import {
  type CartBreakdownLines,
  calculatedItemPricesPence,
  cartBreakdownPence,
  cartPaidWithDiscountPence,
  cartPaidWithoutDiscountPence,
  freeDeliveryThresholdPence,
} from './totals';

/** Delivery price of a single order, in pence. */
const getOrderDeliveryFeePence = (
  order: CartCalculationOrderResponse,
): number => {
  const { delivery_info } = order;

  return company.convertedPricesEnabled
    ? (delivery_info.converted_delivery_price ??
        delivery_info.converted_paid_price ??
        delivery_info.paid_price)
    : delivery_info.paid_price;
};

/**
 * SKUs per parcel, or an empty list when the cart ships as a single parcel.
 * A parcel is one delivery of a group — the part of the cart coming from a
 * single partner warehouse.
 */
const resolveDeliveryParcels = (
  response: CartCalculationResponse,
): string[][] => {
  const parcels = (response.delivery_groups ?? []).flatMap(
    ({ deliveries }) => deliveries ?? [],
  );

  if (parcels.length < 2) {
    return [];
  }

  return parcels.map(({ items }) => items.map(({ sku }) => sku));
};

/**
 * Delivery price charged once for the whole cart, or undefined when the cart has
 * no grouped delivery.
 *
 * Companies without grouped delivery still receive `deliveryGroupPrice: 0` from
 * the backend, so a zero is treated as "no group price" and the per-order
 * delivery fee is used instead — otherwise it would mask a real fee. A grouped
 * delivery that is genuinely free resolves to the same zero either way.
 */
const resolveDeliveryGroupPrice = (
  response: CartCalculationResponse,
  useConverted: boolean,
): number | undefined => {
  const groups = response.delivery_groups;

  if (groups?.length) {
    return (
      groups.reduce(
        (acc, group) =>
          acc +
          ((useConverted
            ? (group.converted_delivery_group_price ??
              group.delivery_group_price)
            : group.delivery_group_price) ?? 0),
        0,
      ) || undefined
    );
  }

  const total = useConverted
    ? (response.convertedDeliveryGroupPrice ?? response.deliveryGroupPrice)
    : response.deliveryGroupPrice;

  return total || undefined;
};

/** What the checkout UI shows for the selected pickup point. */
export interface V3SelectedPupDisplay {
  name: string;
  address: string;
  lat: number | null;
  lng: number | null;
  /** wms point type (`POSTAMAT`, `ISSUE_POINT`, …); picks the map pin colour. */
  type?: string;
}

/**
 * Pickup point being selected: an LMS `V3AvailablePup`, or any wms-directory
 * point (then `id` is unknown and calculate resolves it by `pup_external_id`).
 */
export interface V3PupSelection {
  id?: string | null;
  code: string;
  name?: string;
  /** wms point type (`POSTAMAT`, `ISSUE_POINT`, …); picks the map pin colour. */
  type?: string;
  location?: { lat: number; lon: number; addressText?: string };
}

/** How the customer wants the order handed over. */
export type V3DestinationMethod = 'courier' | 'pickup';

/** Alert text per V3 calculate rejection code; anything else gets the generic one. */
const V3_CALCULATE_FAILURE_MESSAGES: Record<string, string> = {
  ITEM_UNAVAILABLE: 'errors:calculateItemUnavailable',
  PICKUP_POINT_NOT_FOUND: 'errors:calculatePickupPointUnavailable',
  INVALID_PUP_SELECTION: 'errors:calculatePickupPointUnavailable',
};

const isUnknownWarehouseSelection = (error: unknown): boolean =>
  error instanceof AxiosError &&
  error.response?.status === 422 &&
  (
    (error.response.data as { errors?: string[] } | undefined)?.errors ?? []
  ).some((message) => /unknown warehouse/i.test(message));

export class CartStore implements ICartStore {
  private _isCalculateEnabled = false;
  private disableCalculateTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped by every enable/disable so a late enable cannot undo a newer disable. */
  private calculateGateSeq = 0;

  cart: CartItem[] = [];

  totalCartPrice: TotalCartPrice = {
    amountLeftToDiscount: '',
    base: '',
    discount: '',
    paid: '',
    paidWithDiscount: '',
    promocodeDiscount: '',
    loyaltyPointsDiscount: '',
    paidBonusesPence: 0,
    paidLoyaltyPointsPence: 0,
    paidWithoutDiscount: '',
    paidWithoutDiscountPence: 0,
    discountPence: 0,
    taxAmount: 0,
    taxPercent: 0,
    serviceFee: 0,
    minimal_order_value_error: undefined,
    customsDutiesFee: undefined,
    registrationFee: undefined,
    customsServiceFee: undefined,
  };

  deliveriesOrders: CartCalculationOrderResponse[] = [];
  /**
   * Company policy for pickup slots on click-and-collect carts, as echoed by the last
   * `/calculate` (`pickup_slots`). Only meaningful once `isPickupSlotsPolicyKnown`;
   * the checkout reads it through `checkoutStore.pickupSlotsPolicy`, which falls back
   * to the ETA answer until then.
   */
  pickupSlotsPolicy: PickupSlotsPolicy = { enabled: false, required: false };

  /** Whether a `/calculate` of the current click-and-collect cart has stated the policy. */
  isPickupSlotsPolicyKnown = false;

  /**
   * Bumped every time the backend re-states the pickup policy through a rejection.
   * The rebuild reaction tracks it, so a rejection that merely confirms the policy
   * already in memory still rebuilds the delivery that caused it — without this, a
   * cart restored from storage with a slot the backend refuses can never be repriced,
   * because the policy value it would be set to is the one already there.
   */
  pickupSlotsPolicyVerdict = 0;

  v3FindToken: string | null = null;
  v3RouteSelection: V3RouteSelectionItem[] = [];
  v3CalculatedOrders: V3CalculateChildOrder[] = [];
  v3AvailablePups: V3AvailablePup[] = [];
  v3Totals: V3Totals | null = null;
  v3BlockingIssueReason: string | null = null;
  v3SelectedPupId: string | null = null;
  private announcedCalculateFailure: string | null = null;
  /** External id (`V3AvailablePup.code`) of the selected PUP, sent as `pup_external_id`. */
  v3SelectedPupExternalId: string | null = null;
  /**
   * Display info of the selected pickup point. Any wms-directory point is
   * selectable, so the point may be absent from `available_pups` — this is the
   * source the checkout cards render the name/address/coords from.
   */
  v3SelectedPupDisplay: V3SelectedPupDisplay | null = null;
  /**
   * Last pickup point the user confirmed. Survives switching to courier
   * delivery, so returning to the pickup tab restores it without reopening
   * the picker.
   */
  v3LastPupSelection: V3PupSelection | null = null;
  /**
   * Destination the customer picked in checkout. Pickup is the default: a new
   * customer is asked for a pickup point first. Unlike `v3SelectedPup*`, it
   * survives leaving checkout, so the pills come back where they were left.
   */
  v3DestinationMethod: V3DestinationMethod = 'pickup';
  /**
   * `deliveryAddress.pickupPointId` the V3 PUP selection was last derived from.
   * Guards the auto-selection so a user who switched back to courier delivery
   * is not forced onto the PUP again on every recalculate.
   */
  private v3SyncedPickupPointId: string | null = null;
  /**
   * Switched on when the user submits checkout while a slot-required route is
   * missing its slot. Drives error styling in V3 order blocks. Reset by a
   * successful slot selection or route change to a non-scheduled template.
   */
  v3ShowSlotValidationErrors: boolean = false;

  /**
   * SKUs of every parcel the cart is split into, one entry per parcel.
   * Empty for carts the backend delivers as a single parcel.
   */
  deliveryParcels: string[][] = [];

  /**
   * Cart-wide totals from the last calculation, in the base currency (pence).
   * Unlike the per-order ones, they already include the delivery group price.
   */
  cartTotalsPence: {
    base_total: number;
    discount_total: number;
    paid_total: number;
  } = { base_total: 0, discount_total: 0, paid_total: 0 };

  availableAmountForCurrentOrder: number = 0;

  paidExternalBonuses: number = 0;

  /** Mindbox loyalty points the customer earns for the current cart. */
  earnedBonusPoints: number = 0;

  addToCartCatch: {
    cartItem: Offer | Product;
    count: number;
    source: AddToCartSource;
  } | null = null;

  changePriceProductList: CartItem[] = [];
  changeCountProductList: CartItem[] = [];
  productsOutOfStockExpandedList: string[] = [];
  isMissingBirthdayErrorCatched: boolean = false;

  readonly cartStockChangesStore: CartStockChanges;
  readonly promotionStore: PromotionStore;

  private declare readonly calculateQuery: Query<CartCalculationResponse>;

  constructor(
    private readonly userStore: UserStore,
    private readonly orderStore: OrderStore,
    private readonly checkoutStore: CheckoutStore,
    private readonly mainStore: MainStore,
    private readonly membership: CartMembershipMarkerSource,
  ) {
    this.promotionStore = new PromotionStore(this, userStore);
    this.cartStockChangesStore = new CartStockChanges(this);

    this.migrateFromCatalogStore();

    makeAutoObservable(this);

    // Instantiate Query AFTER makeAutoObservable so that _isCalculateEnabled,
    // cart, and other own-state observables are reactive when the Query sets
    // up its internal reaction. Otherwise the reaction captures their initial
    // values as plain props and subsequent mutations don't re-trigger it.
    // The `declare` modifier keeps this property invisible to makeAutoObservable.
    (
      this as unknown as { calculateQuery: Query<CartCalculationResponse> }
    ).calculateQuery = new Query<CartCalculationResponse>(undefined, () => ({
      queryKey: cartQueryKeys.calculate(this.cartHash, this.calculateDepsHash),
      queryFn: () => this.executeCalculate(),
      // Switcher companies only: while an ETA is in flight the delivery method
      // has already flipped (synchronously) but deliveries/zone/warehouse are
      // still scoped to the previous method, so a calculate built now would ship
      // a mismatched payload the backend rejects with a 500. Hold the query
      // until requestETA settles and calculateDeliveries realigns those fields;
      // it then fires exactly once with a consistent payload.
      enabled:
        this._isCalculateEnabled &&
        this.cart.length > 0 &&
        !(company.showDeliveryMethodSwitcher && this.orderStore.isETALoading),
      staleTime: 0,
      gcTime: 5 * 60_000,
      placeholderData: keepPreviousData,
      // LMS answers a rejected payload with a 4xx that no retry will change;
      // other companies keep the one blanket retry they always had.
      retry: (failureCount, error) => {
        if (failureCount >= 1) {
          return false;
        }
        if (!company.hasNewLogistics) {
          return true;
        }
        const status =
          error instanceof AxiosError ? error.response?.status : undefined;
        return !(status && status >= 400 && status < 500);
      },
      // Coalesce the two-phase cart→deliveries update into one refetch.
      // A cart mutation changes cartHash immediately, then the reaction in
      // stores/index.ts runs checkoutStore.calculateDeliveries() which
      // updates deliveries.itemsIds (tracked via calculateDepsHash). Without
      // a delay the query fires twice. 50ms is enough for the follow-up
      // reaction to settle and is imperceptible to the user.
      dynamicOptionsUpdateDelay: 50,
    }));

    reaction(
      () => this.cart.some(({ count }) => count <= 0),
      (hasZeroCount) => {
        if (hasZeroCount) {
          this.cart = this.cart.filter(({ count }) => count > 0);
        }
      },
    );

    reaction(
      () =>
        this.cart.some(
          (item) =>
            item.count >
            capByOrderLimit(
              item.count,
              company.maxQuantityPerSku,
              item.maxQuantityPerOrder,
            ),
        ),
      (hasOverflow) => {
        if (hasOverflow) {
          this.cart = this.cart.map((item) => {
            const cap = capByOrderLimit(
              item.count,
              company.maxQuantityPerSku,
              item.maxQuantityPerOrder,
            );
            return item.count > cap ? { ...item, count: cap } : item;
          }) as CartItem[];
        }
      },
      { fireImmediately: true },
    );

    reaction(
      () => ({
        data: this.calculateQuery.result.data,
        isPlaceholder: this.calculateQuery.result.isPlaceholderData,
      }),
      ({ data, isPlaceholder }) => {
        if (data && !isPlaceholder) {
          runInAction(() => {
            this.isMissingBirthdayErrorCatched = false;
            this.processCalculateResponse(data);
          });
        }
      },
    );

    makePersistable(this, {
      name: 'CartStore',
      properties: [
        'cart',
        'totalCartPrice',
        'v3DestinationMethod',
        'v3LastPupSelection',
      ],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  // Computed getters

  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  /** True when the cart ships as several parcels. */
  get isSplitIntoParcels(): boolean {
    return this.deliveryParcels.length > 1;
  }

  /** Delivery fee for the whole cart, in pence. */
  get totalDeliveryFeePence(): number {
    const groupPrice = this.totalCartPrice.deliveryGroupPrice;

    if (groupPrice !== undefined) {
      return groupPrice;
    }

    return this.deliveriesOrders.reduce(
      (acc, order) =>
        order.is_delivery_free ? acc : acc + getOrderDeliveryFeePence(order),
      0,
    );
  }

  /**
   * Delivery fee to show for one delivery panel, in pence. The group price is
   * only attributable when a single delivery is on screen.
   */
  getDeliveryFeePence(order: CartCalculationOrderResponse): number {
    const groupPrice = this.totalCartPrice.deliveryGroupPrice;
    const isSingleDelivery = (this.checkoutStore.deliveries?.length ?? 1) <= 1;

    if (groupPrice !== undefined && isSingleDelivery) {
      return groupPrice;
    }

    return getOrderDeliveryFeePence(order);
  }

  /**
   * Cart and checkout breakdown lines that show the member discount once.
   * Takes the member discount the screen actually shows — the amount of its
   * row, `0` while the row is hidden — so that the lines always add up to the
   * total.
   */
  breakdownLinesPence(shownMemberDiscountPence: number): CartBreakdownLines {
    return cartBreakdownPence({
      discountPence: this.totalCartPrice.discountPence,
      paidWithoutDiscountPence: this.totalCartPrice.paidWithoutDiscountPence,
      memberDiscountPence: shownMemberDiscountPence,
    });
  }

  get localTotal(): number {
    let total = 0;

    for (const item of this.selectedCartItems) {
      total += this.getEffectiveItemPrice(item) * item.count;
    }

    return total;
  }

  get totalCartCount(): number {
    let totalCount = 0;
    for (let i = 0; i < this.cart.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      totalCount += this.cart[i].count;
    }
    return totalCount;
  }

  get selectedCartItems() {
    return this.cart.filter(({ selected }) => selected);
  }

  get isSelectedAll() {
    return this.cart.every(({ selected }) => selected);
  }

  get selectedItemsCount() {
    return this.cart.filter(({ selected }) => selected).length;
  }

  get selectedItemsTotalCount() {
    return this.selectedCartItems.reduce<number>(
      (total, { count }) => total + count,
      0,
    );
  }

  get mixAndMatchProgress(): MixAndMatchProgress | null {
    if (!company.mixAndMatchEnabled) {
      return null;
    }

    return getMixAndMatchProgress(this.selectedCartItems);
  }

  get mixAndMatchUnits(): number {
    return countMixAndMatchUnits(this.selectedCartItems);
  }

  get isMaxItemsPerOrderExceeded(): boolean {
    const limit = company.maxItemsPerOrder;
    return limit !== undefined && this.selectedItemsTotalCount > limit;
  }

  get finalPrice() {
    return this.totalCartPrice.paid || '0';
  }

  get cartForFirebase(): FirebaseCartItem[] {
    return this.cart.map((item, i): FirebaseCartItem => {
      return this.convertToFirebase(item, i);
    });
  }

  get selectedCartForFirebase(): FirebaseCartItem[] {
    return this.selectedCartItems.map((item, i): FirebaseCartItem => {
      return this.convertToFirebase(item, i);
    });
  }

  get isRestrictedItemInCart(): boolean {
    const commodityGroupList: ProductCommodityGroupAPI[] = [
      ProductCommodityGroupAPI.TOBACCO,
      ProductCommodityGroupAPI.ALCOHOL,
    ];

    for (let i = 0; i < this.cart.length; i++) {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      if (commodityGroupList.includes(this.cart[i].commodityGroup)) {
        return true;
      }
    }

    return false;
  }

  get hasRestrictedCategoriesInCart(): boolean {
    const restrictedCategories = settingStore.restrictedCategories;

    if (!restrictedCategories.length) {
      return false;
    }

    const disallowedCommodityGroups = settingStore.restrictedCategories.map(
      (item) => item.name,
    );
    const commodityGroups = this.selectedCartItems
      .map((item) => item.commodityGroup?.name)
      .filter(Boolean) as string[];

    if (
      disallowedCommodityGroups.some((category) =>
        commodityGroups.includes(`${category}`),
      )
    ) {
      return true;
    }

    return false;
  }

  get isAdultItemInCart(): boolean {
    return this.cart.some((item) => item.properties.age_restriction);
  }

  get isAdultCommodityInCart(): boolean {
    return this.cart.some((item) => {
      const commodityGroupId = item.commodityGroup?.id;

      return commodityGroupId === 2 || commodityGroupId === 3;
    });
  }

  get leftUntilFreeDelivery(): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (this.userStore.personalData.freeDeliveryDaysLeft > 0) {
      return zero;
    }

    return this.leftUntilFreeFee(
      this.mainStore.convertPoundsToPence(this.totalCartPrice.paidWithDiscount),
      freeDeliveryThresholdPence(
        this.deliveriesOrders,
        this.orderStore.fee.threshold || 0,
      ),
    );
  }

  get leftUntilFreeMinimalOrderFee(): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (this.userStore.isStaff) {
      return zero;
    }

    if (this.orderStore.isFirstOrder) {
      return zero;
    }

    const paid = this.mainStore.convertPoundsToPence(
      this.totalCartPrice.paidWithDiscount,
    );

    return this.leftUntilFreeFee(paid, company.config.minimalOrderFeeThreshold);
  }

  get isStoreInMinimalOrderList() {
    return company.config.warehouse.minimalOrderFeeWarehouseCodes.includes(
      this.orderStore.etaWarehouseCode,
    );
  }

  get isMinimalOrderFeePassed() {
    if (this.userStore.isStaff) {
      return true;
    }

    if (this.orderStore.isFirstOrder) {
      return true;
    }

    if (!this.isStoreInMinimalOrderList) {
      return true;
    }

    if (this.cart.length === 0) {
      return false;
    }

    return this.mainStore.isZero(this.leftUntilFreeMinimalOrderFee[0]);
  }

  get isPromoCodeApplied() {
    return (
      this.promocode.success &&
      parseFloat(this.totalCartPrice.promocodeDiscount) > 0 &&
      this.mainStore.isZero(this.totalCartPrice.amountLeftToDiscount)
    );
  }

  get isFixedPromoCode() {
    return this.promocode.coupon?.type === 'FIXED';
  }

  get formatPromocodeDiscountAmount() {
    const amount = this.promocode.coupon?.value || 0;

    if (this.mainStore.isZero(amount)) {
      return '0';
    }

    if (this.promocode.coupon?.type === 'PERCENTAGE') {
      return `${amount}%`;
    }

    return this.mainStore.addCurrencySymbol(
      this.mainStore.convertPenceToPounds(amount),
    );
  }

  get promocode(): Promocode {
    return this.promotionStore.promocode;
  }

  // Methods

  setCartItemCountByProduct(
    cartItem: Offer | Product,
    count: number,
    action: 'add' | 'remove',
    source: AddToCartSource,
    lvl3category?: number,
  ) {
    const isBundle = isProductBundle(cartItem);

    analyticsService.track(AnalyticsEvent.CART_CHANGED, {
      cart_id: undefined,
      type: 'stock',
      products_amount: this.cart.reduce((sum, item) => sum + item.count, 0),
      items_amount: this.cart.length,
      price: this.totalCartPrice.base,
      final_price: this.finalPrice,
      eta_min: this.orderStore.etaCalculation?.duration.min || 0,
      eta_max: this.orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: this.orderStore.fee.shippingPounds || 0,
      threshold: this.orderStore.fee.thresholdPounds || 0,
      is_surger: this.orderStore.etaCalculation?.highDemand || false,
      stage: 'checkout',
    });
    if (count < 0) {
      count = 0;
    }

    count = capByOrderLimit(
      count,
      company.maxQuantityPerSku,
      cartItem.maxQuantityPerOrder,
    );

    if (
      isItemAgeRestricted(cartItem) &&
      !this.userStore.personalData.isAdult &&
      action === 'add' &&
      count
    ) {
      this.addToCartCatch = { cartItem, count, source };
      this.mainStore.setIsAgeRestrictionPopover(true);
      return;
    }

    const itemState = [];
    const { currentWhSellable, laterCount } = getProductLimit(cartItem);
    const isExpress =
      currentWhSellable && this.orderStore.isExpressAvailableNow;

    if (action === 'add' && currentWhSellable + 1 === count && isExpress) {
      itemState.push(
        i18n.t('expressAvailable', {
          expressItems: currentWhSellable,
          standardItems: laterCount,
        }),
      );
    }

    if (action === 'add' && !isBundle && cartItem.multibuyPriceLevels?.length) {
      const sortedLevels = sortMultibuyLevels(cartItem.multibuyPriceLevels);

      const reachedLevel = sortedLevels.find(
        (level) => count === level.quantity,
      );

      if (reachedLevel) {
        itemState.push(i18n.t('phrases:discountReached'));
      } else {
        const nextLevel = sortedLevels.find((level) => count < level.quantity);

        if (nextLevel) {
          itemState.push(
            i18n.t('phrases:addMoreToDiscount', {
              count: nextLevel.quantity - count,
            }),
          );
        }
      }
    }

    if (itemState.length) {
      const message = itemState.join('<br /><br/>');

      this.mainStore.popAlert();
      this.mainStore.pushAlert('success', message);
    }

    const itemIndex = this.cart.findIndex(({ id, sku }) =>
      isBundle ? id && id === cartItem.id : sku && sku === cartItem.sku,
    );
    const isNewProduct = itemIndex === -1;
    if (!isNewProduct) {
      if (!count) {
        this.cart.splice(itemIndex, 1);

        if (!this.cart.length) {
          this.promotionStore.resetPromocode();
        }
      } else {
        const item = this.cart[itemIndex];

        if (!item) {
          return;
        }

        this.cart[itemIndex] = {
          ...item,
          count,
          maxQuantityPerOrder: cartItem.maxQuantityPerOrder,
          multibuyPriceLevels: cartItem.multibuyPriceLevels,
        };
        (this.cart as IObservableArray<CartItem>).replace(this.cart);
      }
    }

    if (count && isNewProduct) {
      this.cart.push({
        ...cartItem,
        count,
        selected: true,
        sku: isBundle ? `${cartItem.id}` : cartItem.sku,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_name: cartItem.name,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_price: cartItem.discountPrice
          ? cartItem.discountPriceFormatted
          : cartItem.priceFormatted,
      });
      this.mainStore.sendToRN('sendTags', {
        cart_product_image: cartItem.previewImageThumb,
      });
    }

    this.mainStore.sendToRN('sendTags', {
      cart_update_time: Math.floor(Date.now() / 1000),
    });

    this.cartStockChangesStore.closeNotifierModal();

    this.trackCartItemChange({
      cartItem,
      count,
      action,
      source,
      isBundle,
      lvl3category,
    });
  }

  emptyCart() {
    this.cart = [];
    this.promotionStore.resetPromocode();
    this.mainStore.sendToRN('removeTag', 'cart_update_time');
    this.mainStore.sendToRN('removeTag', 'cart_product_name');
    this.mainStore.sendToRN('removeTag', 'cart_product_price');
    this.mainStore.sendToRN('removeTag', 'cart_product_image');
    this.calculateQuery.reset();
  }

  confirmAgeAndProcessPendingItem() {
    this.userStore.setIsAdult(true);
    this.mainStore.setIsAgeRestrictionPopover(false);

    const pending = this.addToCartCatch;
    this.addToCartCatch = null;

    if (pending) {
      this.setCartItemCountByProduct(
        pending.cartItem,
        pending.count,
        'add',
        pending.source,
      );
    }
  }

  removeCartItemByOfferSKU(skuToRemove: string) {
    this.cart = this.cart.filter(({ sku }) => sku !== skuToRemove);
  }

  removeCartItemByOfferId(offerId: number) {
    this.cart = this.cart.filter(({ id }) => id !== offerId);
  }

  emptySelectedItemsFromCart() {
    if (this.selectedItemsCount === 0) {
      return;
    }
    if (this.cart.length === this.selectedItemsCount) {
      this.cart = [];
    } else {
      this.cart = this.cart.reduce((acc: CartItem[], item) => {
        if (!item.selected) {
          acc.push({ ...item, selected: true });
        }
        return acc;
      }, []);
    }
  }

  handleSelectedCartItem = (sku: string) => {
    const currentItemIndex = this.cart.map((i) => i.sku).indexOf(sku);
    const currentItem = this.cart[currentItemIndex];

    if (!currentItem) {
      return;
    }

    this.cart[currentItemIndex] = {
      ...currentItem,
      selected: !currentItem.selected,
    };
  };

  handleSelectAllCartItems = () => {
    this.cart = this.cart.map((item) => ({
      ...item,
      selected: !this.isSelectedAll,
    }));
  };

  getCartItemById(item: Offer | Product): CartItem | undefined {
    const id = item.id;
    const sku = item.sku;
    const isBundle = isProductBundle(item);

    return this.cart.find((cartItem) => {
      const typeIsEqual = isBundle
        ? (cartItem as Product).productType === 'bundle'
        : (cartItem as Product).productType !== 'bundle';
      const idIsEqual = isBundle
        ? (cartItem.id && cartItem.id === id) ||
          (cartItem.sku && cartItem.sku === sku)
        : cartItem.sku && cartItem.sku === sku;

      return idIsEqual && typeIsEqual;
    });
  }

  getCartItemCountById(item: Offer | Product): number {
    if (!item) {
      return 0;
    }

    return this.getCartItemById(item)?.count ?? 0;
  }

  get calculationProcess(): CalculationProcess {
    return {
      isLoading: this.calculateQuery.isFetching,
      isError: this.calculateQuery.isError,
    };
  }

  get v3HasBlockingIssue(): boolean {
    return !!this.v3BlockingIssueReason;
  }

  get isCalculating(): boolean {
    return this.calculateQuery.isFetching;
  }

  /**
   * What a recalculation would be keyed on right now.
   *
   * Exposed so a caller that changes state expecting a recalculation can tell
   * whether one will actually follow: the query refetches on a change of key,
   * and a change that leaves the key identical starts nothing. A caller that
   * assumed otherwise left the checkout waiting on a recalculation that was
   * never going to happen.
   */
  get calculateKey(): string {
    return `${this.cartHash}|${this.calculateDepsHash}`;
  }

  /** Whether a change of {@link calculateKey} would start a request at all. */
  get isCalculateEnabled(): boolean {
    return this._isCalculateEnabled && this.cart.length > 0;
  }

  get isPlaceholderData(): boolean {
    return this.calculateQuery.isPlaceholderData;
  }

  get isCalculateError(): boolean {
    return this.calculateQuery.isError;
  }

  async enableCalculate(): Promise<void> {
    if (this.disableCalculateTimer) {
      clearTimeout(this.disableCalculateTimer);
      this.disableCalculateTimer = null;
    }
    const seq = ++this.calculateGateSeq;
    await this.orderStore.reCalculateDeliveries();
    if (seq === this.calculateGateSeq) {
      runInAction(() => {
        this._isCalculateEnabled = true;
      });
    }
  }

  /**
   * Deferred by a macrotask: cart -> checkout unmounts one page and mounts the
   * next in the same commit, and a synchronous off/on would make the query
   * observer refetch an unchanged key once the checkout re-enables it.
   */
  disableCalculate(): void {
    this.calculateGateSeq += 1;
    if (this.disableCalculateTimer) {
      return;
    }
    this.disableCalculateTimer = setTimeout(() => {
      this.disableCalculateTimer = null;
      runInAction(() => {
        this._isCalculateEnabled = false;
      });
    }, 0);
  }

  /**
   * Selects/clears a PUP destination for V3 calculate. The destination is part
   * of calculateDepsHash, so totals/routes recalculate automatically. Points
   * outside `available_pups` are selected by `code` alone — calculate resolves
   * their `pup_id` (synced back in `calculateCartV3`).
   */
  selectV3Pup = (pup: V3PupSelection | null) => {
    const pupId = pup?.id ?? null;
    const pupExternalId = pup?.code ?? null;
    this.v3DestinationMethod = pup ? 'pickup' : 'courier';
    if (pup) {
      this.v3LastPupSelection = { ...pup };
    }
    this.v3SelectedPupDisplay =
      pup && (pup.name || pup.location)
        ? {
            name: pup.name ?? '',
            address: pup.location?.addressText ?? '',
            lat: pup.location?.lat ?? null,
            lng: pup.location?.lon ?? null,
            type: pup.type,
          }
        : null;
    if (
      this.v3SelectedPupId === pupId &&
      this.v3SelectedPupExternalId === pupExternalId
    ) {
      return;
    }
    this.v3SelectedPupId = pupId;
    this.v3SelectedPupExternalId = pupExternalId;
    this.resetV3RouteSelection();
  };

  /**
   * Clears the PUP destination without waiting for a recalculate. Called when
   * the user leaves checkout — the next calculate fired from elsewhere on the
   * site will go out with `destination.kind=CUSTOMER` as the spec requires.
   */
  resetV3PupSelection = () => {
    if (
      this.v3SelectedPupId !== null ||
      this.v3SelectedPupExternalId !== null
    ) {
      this.v3SelectedPupId = null;
      this.v3SelectedPupExternalId = null;
    }
    this.v3SelectedPupDisplay = null;
    this.v3SyncedPickupPointId = null;
  };

  /** Moves the checkout pills without touching the selected point (the picker opens right after). */
  setV3DestinationMethod = (method: V3DestinationMethod) => {
    this.v3DestinationMethod = method;
  };

  /**
   * Re-applies the last confirmed pickup point when checkout is entered with
   * the pickup method and nothing selected: the selection is cleared on leaving
   * checkout so that the cart page calculates for the customer address.
   */
  restoreV3PupSelection = () => {
    if (
      this.v3DestinationMethod !== 'pickup' ||
      this.v3SelectedPupId ||
      this.v3SelectedPupExternalId ||
      !this.v3LastPupSelection
    ) {
      return;
    }
    this.selectV3Pup(this.v3LastPupSelection);
  };

  /**
   * Pre-selects the PUP matching the pickup point chosen in the address modal:
   * `deliveryAddress.pickupPointId` is the external id, i.e. the LMS pup `code`.
   * Runs once per pickup point so the user can still switch back to courier
   * delivery in checkout.
   */
  private syncV3PupWithDeliveryAddress(pups: V3AvailablePup[]): void {
    const pickupPointId =
      this.userStore?.deliveryAddress?.pickupPointId ?? null;
    if (!pickupPointId || pickupPointId === this.v3SyncedPickupPointId) {
      return;
    }
    const match = pups.find((pup) => pup.code === pickupPointId);
    if (!match) {
      return;
    }
    this.v3SyncedPickupPointId = pickupPointId;
    if (this.v3SelectedPupId !== match.id) {
      this.v3DestinationMethod = 'pickup';
      this.v3SelectedPupId = match.id;
      this.v3SelectedPupExternalId = match.code;
      this.v3SelectedPupDisplay = {
        name: match.name,
        address: match.location?.addressText ?? '',
        lat: match.location?.lat ?? null,
        lng: match.location?.lon ?? null,
      };
      this.resetV3RouteSelection();
    }
  }

  resetV3RouteSelection = () => {
    if (this.v3RouteSelection.length > 0) {
      this.v3RouteSelection = [];
    }
    this.v3ShowSlotValidationErrors = false;
    this.v3BlockingIssueReason = null;
  };

  resetV3CalculatedOrders = () => {
    this.v3CalculatedOrders = [];
    this.v3AvailablePups = [];
    this.v3Totals = null;
    this.v3FindToken = null;
  };

  setV3ShowSlotValidationErrors = (value: boolean) => {
    this.v3ShowSlotValidationErrors = value;
  };

  selectV3Slot = (
    warehouseId: string,
    scheduleSlotId: string,
    currentDate: number,
  ) => {
    this.v3RouteSelection = this.v3RouteSelection.map((sel) =>
      sel.warehouse_id === warehouseId
        ? {
            ...sel,
            slot_delivery_details: {
              schedule_slot_id: scheduleSlotId,
              current_date: currentDate,
            },
          }
        : sel,
    );

    this.refreshV3BlockingIssue();
  };

  /**
   * Updates the selected route template for a given warehouse.
   * If the chosen route is non-scheduled, any previous slot details are cleared.
   * Re-evaluates the blocking issue flag (slot-required-but-not-supported) on every change.
   */
  selectV3Route = (warehouseId: string, templateId: string) => {
    const child = this.v3CalculatedOrders.find(
      (o) => o.warehouse_id === warehouseId,
    );
    const route = child?.available_routes.find(
      (r) => r.template_id === templateId,
    );

    this.v3RouteSelection = this.v3RouteSelection.map((sel) =>
      sel.warehouse_id === warehouseId
        ? {
            ...sel,
            template_id: templateId,
            slot_delivery_details: route?.slot_selection_required
              ? sel.slot_delivery_details
              : undefined,
          }
        : sel,
    );

    this.refreshV3BlockingIssue();
  };

  private refreshV3BlockingIssue(): void {
    const slotRequiredButMissing = this.v3RouteSelection.some((sel) => {
      const childOrder = this.v3CalculatedOrders.find(
        (o) => o.warehouse_id === sel.warehouse_id,
      );
      const selectedRoute = childOrder?.available_routes.find(
        (r) => r.template_id === sel.template_id,
      );
      return (
        selectedRoute?.slot_selection_required && !sel.slot_delivery_details
      );
    });
    this.v3BlockingIssueReason = slotRequiredButMissing
      ? 'SLOT_REQUIRED_NOT_SUPPORTED'
      : null;

    if (!slotRequiredButMissing) {
      this.v3ShowSlotValidationErrors = false;
    }
  }

  async calculateCart(options?: { throwOnError?: boolean }): Promise<boolean> {
    if (!this.cart.length) return false;
    this.announcedCalculateFailure = null;
    const result = await this.calculateQuery.refetch();
    if (result.isError && options?.throwOnError) {
      throw result.error instanceof Error
        ? result.error
        : new Error('Cart calculation failed');
    }
    return (
      this.changePriceProductList.length > 0 ||
      this.changeCountProductList.length > 0
    );
  }

  destroy() {
    this.cartStockChangesStore.destroy();
    this.calculateQuery.destroy();
  }

  convertToFirebase(
    item: (Product | Offer) & { count?: number },
    i: number = 0,
  ): FirebaseCartItem {
    return {
      item_id: isProductBundle(item)
        ? item.id.toString()
        : (item as Offer).sku || item.id.toString(),
      item_name: item.name,
      quantity: item.count || 1,
      promotion_id: '',
      promotion_name: '',
      affiliation: '',
      coupon: this.promocode.value,
      creative_name: '',
      creative_slot: '',
      discount: item.discountPrice
        ? this.mainStore.toFloat(
            this.mainStore.convertPenceToPounds(
              item.price - item.discountPrice,
            ),
          )
        : 0,
      index: i,
      item_brand: '',
      item_category: item.categoryName || '',
      item_list_name: item.categoryName || '',
      item_list_id: item.categoryId?.toString() || '',
      item_variant: '',
      location_id: '',
      tax: 0,
      price: this.mainStore.toFloat(item.priceFormatted),
      currency: this.orderStore.currency.toUpperCase(),
    };
  }

  // Private methods

  private trackCartItemChange(params: {
    cartItem: Offer | Product;
    count: number;
    action: 'add' | 'remove';
    source: AddToCartSource;
    isBundle: boolean;
    lvl3category?: number;
  }): void {
    const { cartItem, count, action, source, isBundle, lvl3category } = params;

    const analyticEvent =
      action === 'add'
        ? AnalyticsEvent.PURCHASE_PRODUCT_ADDED_TO_CART
        : AnalyticsEvent.PURCHASE_PRODUCT_REMOVED_FROM_CART;
    const sourceList = source.id
      ? { id: source.id, name: source.name ?? '' }
      : {
          id: String(cartItem.categoryId ?? ''),
          name: cartItem.categoryName ?? '',
        };
    const productsAmount = this.cart.reduce((sum, item) => sum + item.count, 0);

    analyticsService.track(analyticEvent, {
      product_id: cartItem.id,
      category_id: lvl3category || cartItem.categoryId,
      lvl1_category_id: undefined,
      lvl2_category_id: cartItem.parentCategoryId,
      source: source.type,
      source_id: source.id ?? '',
      source_name: source.name ?? '',
      quantity: count,
      cart_id: undefined,
      products_amount: productsAmount,
      items_amount: this.cart.length,
      price: this.totalCartPrice.base,
      final_price: this.finalPrice,
      eta_min: this.orderStore.etaCalculation?.duration.min || 0,
      eta_max: this.orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: this.orderStore.fee.shippingPounds || 0,
      threshold: this.orderStore.fee.thresholdPounds || 0,
      is_surger: this.orderStore.etaCalculation?.highDemand || false,
    });

    analyticsService.track(AnalyticsEvent.PIXEL_ADD_TO_CART, {
      content_ids: [cartItem.id],
      content_type: 'product',
      currency: company.currency,
      value: this.mainStore.toFloat(cartItem.priceFormatted),
    });

    const firebaseEvent =
      action === 'add'
        ? AnalyticsEvent.ADD_TO_CART
        : AnalyticsEvent.REMOVE_FROM_CART;
    analyticsService.track(firebaseEvent, {
      currency: this.orderStore.currency.toUpperCase(),
      items: [
        {
          item_id: isBundle
            ? cartItem.id
            : (cartItem as Offer).sku || cartItem.id,
          item_name: cartItem.name,
          quantity: 1,
          promotion_id: '',
          promotion_name: '',
          affiliation: '',
          coupon: this.promocode.value,
          creative_name: '',
          creative_slot: '',
          discount: cartItem.discountPrice
            ? this.mainStore.toFloat(
                this.mainStore.convertPenceToPounds(
                  cartItem.price - cartItem.discountPrice,
                ),
              )
            : 0,
          index: 0,
          item_brand: '',
          item_category: cartItem.categoryName,
          item_list_name: sourceList.name,
          item_list_id: sourceList.id,
          item_variant: '',
          location_id: '',
          tax: 0,
          price: this.mainStore.toFloat(cartItem.priceFormatted),
          currency: this.orderStore.currency.toUpperCase(),
        },
      ],
      value: this.mainStore.toFloat(cartItem.priceFormatted),
    });
  }

  private getEffectiveItemPrice(item: CartItem): number {
    if (isProductBundle(item)) {
      return (item as CartItemBundle).price;
    }
    const multibuyLevel = getApplicableMultibuyLevel(
      (item as CartItemOffer).multibuyPriceLevels,
      item.count,
    );
    if (multibuyLevel) return multibuyLevel.unitPrice;
    return (item as CartItemOffer).discountPrice || item.price;
  }

  private leftUntilFreeFee(
    totalPrice: number,
    feeThreshold: number,
  ): [string, string] {
    const zero: [string, string] = ['0', '100%'];
    if (!this.cart.length) {
      return zero;
    }

    if (totalPrice >= feeThreshold) {
      return zero;
    }
    const remainder = feeThreshold - totalPrice;
    const remainderPercent = 100 - (remainder * 100) / (feeThreshold || 1);
    return [
      this.mainStore.convertPenceToPounds(remainder),
      remainderPercent + '%',
    ];
  }

  /**
   * One-time migration: move cart data from old CatalogStore localStorage key
   * to the new CartStore key. Runs before makePersistable so hydration reads
   * the migrated data.
   */
  private migrateFromCatalogStore(): void {
    if (typeof window === 'undefined') return;

    try {
      const cartStoreRaw = window.localStorage.getItem('CartStore');
      if (cartStoreRaw) {
        const cartStoreData = JSON.parse(cartStoreRaw);
        if (cartStoreData.cart?.length) return;
      }

      const catalogRaw = window.localStorage.getItem('CatalogStore');
      if (!catalogRaw) return;

      const catalogData = JSON.parse(catalogRaw);
      if (!catalogData.cart?.length) return;

      window.localStorage.setItem(
        'CartStore',
        JSON.stringify({ cart: catalogData.cart }),
      );

      delete catalogData.cart;
      window.localStorage.setItem('CatalogStore', JSON.stringify(catalogData));
    } catch {
      // Silent fail — user starts with empty cart
    }
  }

  private get cartHash(): string {
    return JSON.stringify(
      this.selectedCartItems.map(({ sku, count }) => ({ sku, count })),
    );
  }

  private get calculateDepsHash(): string {
    const deliveryMethod = this.checkoutStore?.deliveryMethod;
    // Read warehouse straight from etaCalculation (not via etaWarehouseCode
    // getter) so cache key and buildCalculateRequest payload reference the
    // exact same field of the same object — prevents a stale default from
    // the getter ending up in either side.
    const etaCalculation = this.orderStore?.etaCalculation;
    const deps = {
      warehouse: etaCalculation?.warehouse.code,
      zone: deliveryMethod
        ? etaCalculation?.warehouse.zone?.[deliveryMethod]?.id
        : undefined,
      deliveryMethod,
      // The payload itself branches on this — v3 sends `address` and
      // `recipient` as null while signed out — so a key without it lets a
      // basket keep showing the totals of a session that has ended, and the
      // account-scoped parts of them (bonuses, loyalty discount, free
      // delivery) along with it.
      isAuthorized: this.userStore?.isAuthorized,
      address: {
        lat: this.userStore?.deliveryAddress?.coordinates.lat,
        lng: this.userStore?.deliveryAddress?.coordinates.lng,
      },
      partnerPup: this.userStore?.deliveryAddress?.partnerPupExternalId,
      // Simplified flow has no coupon yet; fall back to raw value so hash invalidates on user input.
      promocode:
        this.promotionStore?.promocode?.coupon?.code ??
        (company.simplifiedPromocodeFlow
          ? this.promotionStore?.promocode?.value || undefined
          : undefined),
      useLoyaltyPoints: this.userStore?.loyaltyPointsStore?.useLoyaltyPoints,
      tips: this.orderStore?.orderTipsValue,
      deliveries: this.checkoutStore?.deliveries?.map((d) => ({
        slot: d.slot?.schedule_slot_id,
        // A schedule_slot_id repeats across days and the payload carries the
        // date alongside it, so without the date the same time slot on another
        // day reuses this key and the cart is never repriced.
        slotDate: d.slot?.current_date,
        items: d.itemsIds,
      })),
      // Either id names the same point; the response back-fills the other one,
      // which must not look like a new destination.
      v3SelectedPup: company.hasNewLogistics
        ? (this.v3SelectedPupExternalId ?? this.v3SelectedPupId)
        : undefined,
      v3RouteSelection: company.hasNewLogistics
        ? this.v3RouteSelection
            .filter((sel) => !this.isImplicitV3RouteSelection(sel))
            .map((sel) => ({
              warehouse: sel.warehouse_id,
              template: sel.template_id,
              slot: sel.slot_delivery_details?.schedule_slot_id,
              slotDate: sel.slot_delivery_details?.current_date,
            }))
        : undefined,
    };
    return calculateDepsKey(deps, this.membership.cartMarker);
  }

  /**
   * A selection that only mirrors the route calculate picks on its own changes
   * nothing in the response, so it stays out of the query key: syncing it from
   * a response would otherwise refetch the very same calculation.
   */
  private isImplicitV3RouteSelection(sel: V3RouteSelectionItem): boolean {
    if (!sel.template_id) {
      return true;
    }
    if (sel.slot_delivery_details) {
      return false;
    }
    const routes =
      this.v3CalculatedOrders.find((o) => o.warehouse_id === sel.warehouse_id)
        ?.available_routes ?? [];
    const defaultTemplateId =
      routes.find((route) => route.is_default)?.template_id ??
      (routes.length === 1 ? routes[0]?.template_id : undefined);

    return defaultTemplateId === sel.template_id;
  }

  private async executeCalculate(): Promise<CartCalculationResponse> {
    const requestData = this.buildCalculateRequest();

    try {
      if (company.hasNewLogistics) {
        return await this.executeCalculateV3();
      }

      const response = await CatalogRequests.calculateCart(requestData);

      runInAction(() => {
        this.announcedCalculateFailure = null;
      });

      return response;
    } catch (error) {
      runInAction(() => {
        this.handleCalculateError(error, requestData);
      });

      // The payload used to be reported on every request, which buried Sentry
      // under ~124k messages a day. Report it only when the call actually
      // breaks, and only for infrastructure failures: 4xx bodies carry business
      // outcomes (stock changes, age restriction, missing birthday) that
      // handleCalculateError already turns into UI.
      const status =
        error instanceof AxiosError ? error.response?.status : undefined;

      if (status === undefined || status >= 500) {
        reportError(DOMAIN.cart, error, {
          reason: REASON.cart.calculateFailed,
          fingerprint: [String(status ?? 'network')],
          extras: { request: requestData, status: status ?? null },
        });
      }

      throw error;
    }
  }

  private buildCalculateRequest(): CartCalculationRequest {
    const selectedItems = this.selectedCartItems.map((item) => toJS(item));

    const deliveries =
      this.checkoutStore.deliveries?.map(({ itemsIds, slot }) => {
        const requestedItems: CartCalculationItemRequest[] = selectedItems
          .filter(
            (item) => itemsIds.includes(item.sku) && !isProductBundle(item),
          )
          .map((item) => ({
            sku: (item as Offer).sku,
            requested_quantity: item.count,
          }));

        const requestedBundles: CartBundlesCalculationItemRequest[] =
          selectedItems
            .filter(
              (item) => itemsIds.includes(item.sku) && isProductBundle(item),
            )
            .map((item) => ({
              id: item.id,
              requested_quantity: item.count,
            }));

        return {
          bundles: requestedBundles,
          items: requestedItems,
          slot_delivery_details: slot && {
            schedule_slot_id: slot.schedule_slot_id,
            current_date: slot.current_date,
          },
        };
      }) ?? [];

    // Read warehouse_code and zone_id from the SAME etaCalculation snapshot
    // so they cannot drift apart. Using the etaWarehouseCode getter here
    // was unsafe: its `deliveryAddress && etaCalculation.warehouse.code`
    // check falls back to company.config.warehouse.default whenever
    // deliveryAddress is momentarily falsy, while zone?.id continues to
    // read from etaCalculation.warehouse.zone — producing a 422
    // "Delivery not available" with mismatched warehouse/zone.
    const etaCalculation = this.orderStore.etaCalculation;
    const zone =
      etaCalculation?.warehouse.zone?.[this.checkoutStore.deliveryMethod];
    const { deliveryAddress, loyaltyPointsStore, mindboxLoyaltyStore } =
      this.userStore;

    const partnerPupExternalId =
      deliveryAddress?.partnerPupExternalId || undefined;
    const partnerPickupProvider = partnerPupExternalId
      ? company.partnerPickupDeliveryProvider
      : undefined;

    // In simplified flow, promocode.value is the source of truth before a coupon is resolved.
    const effectivePromocodeCode =
      this.promocode.coupon?.code ??
      (company.simplifiedPromocodeFlow
        ? this.promocode.value || undefined
        : undefined);

    const requestData: CartCalculationRequest = {
      address: {
        latitude:
          deliveryAddress?.coordinates.lat ?? company.config.map.center.lat,
        longitude:
          deliveryAddress?.coordinates.lng ?? company.config.map.center.lng,
      },
      deliveries,
      seller: 'jiffy',
      should_use_loyalty_points:
        (effectivePromocodeCode && !loyaltyPointsStore.workWithPromocode) ||
        mindboxLoyaltyStore.enabled
          ? false
          : loyaltyPointsStore.useLoyaltyPoints,
      should_use_bonuses: mindboxLoyaltyStore.enabled
        ? loyaltyPointsStore.useLoyaltyPoints
        : false,
      warehouse_code:
        etaCalculation?.warehouse.code || company.config.warehouse.default,
      zone_id: partnerPickupProvider ? undefined : zone?.id,
      promocode:
        loyaltyPointsStore.useLoyaltyPoints &&
        !loyaltyPointsStore.workWithPromocode
          ? undefined
          : effectivePromocodeCode,
      tips_amount: this.mainStore.convertPoundsToPence(
        this.orderStore.orderTipsValue,
      ),
    };

    const deliveryMethod = this.checkoutStore.deliveryMethod;
    const mechanic =
      this.checkoutStore.deliveryMethod === DeliveryMethods.CLICK_AND_COLLECT
        ? DeliveryMechanic.PICKUP
        : deliveries.some((item) => !!item.slot_delivery_details)
          ? DeliveryMechanic.SCHEDULED
          : DeliveryMechanic.ON_DEMAND;

    if (partnerPickupProvider) {
      requestData.delivery_provider = partnerPickupProvider;
      requestData.delivery_method = DeliveryMethods.COURIER_DELIVERY;
      requestData.delivery_mechanic = DeliveryMechanic.ON_DEMAND;
      requestData.destination_kind = 'pup';
      requestData.pup_type = 'partner';
      requestData.pup_provider = partnerPickupProvider;
      requestData.pup_external_id = partnerPupExternalId;
      requestData.pup_id = deliveryAddress?.pickupPointId || undefined;

      return requestData;
    }

    requestData.delivery_provider =
      this.orderStore.deliveryStore.deliveryProviderByMethod[deliveryMethod] ||
      undefined;
    requestData.delivery_method = deliveryMethod;
    requestData.delivery_mechanic = mechanic;
    // biome-ignore lint/suspicious/noConsole: temporary JS-13586 trace
    console.warn('[JS-13586] calculate', {
      delivery_provider: requestData.delivery_provider,
      delivery_method: deliveryMethod,
      providers: { ...this.orderStore.deliveryStore.deliveryProviderByMethod },
    });

    return requestData;
  }

  /**
   * A parcel whose every line left the cart no longer exists for calculate,
   * and a selection naming its warehouse is rejected as unknown. Judged by the
   * last response; without one nothing is known and every selection stays.
   */
  private isV3RouteSelectionInCart(
    sel: V3RouteSelectionItem,
    cartItems: CartItem[],
  ): boolean {
    if (this.v3CalculatedOrders.length === 0) {
      return true;
    }
    const order = this.v3CalculatedOrders.find(
      (o) => o.warehouse_id === sel.warehouse_id,
    );
    if (!order) {
      return false;
    }
    const skus = new Set<string>();
    const bundleIds = new Set<string>();
    for (const item of cartItems) {
      if (isProductBundle(item)) {
        bundleIds.add(String(item.id));
      } else {
        skus.add((item as CartItemOffer).sku);
      }
    }
    return (
      order.items.some((item) => skus.has(item.sku)) ||
      order.bundles.some((bundle) => bundleIds.has(String(bundle.id)))
    );
  }

  /**
   * The parcel split is the backend's call: when it no longer contains a
   * warehouse the selection names, the same request is repeated with no
   * selection and the response rebuilds it.
   */
  private async requestV3Calculate(
    request: V3CalculateRequest,
  ): Promise<V3CalculateResponse> {
    try {
      return await V3Requests.calculate(request);
    } catch (error) {
      if (!request.routeSelection || !isUnknownWarehouseSelection(error)) {
        throw error;
      }
      const { routeSelection: _stale, ...withoutSelection } = request;
      return await V3Requests.calculate(withoutSelection);
    }
  }

  private async executeCalculateV3(): Promise<CartCalculationResponse> {
    const { deliveryAddress, loyaltyPointsStore } = this.userStore;
    const selectedItems = this.selectedCartItems.map((item) => toJS(item));

    const items: V3OrderItemInput[] = [];
    const bundles: V3OrderBundleInput[] = [];
    selectedItems.forEach((item) => {
      if (isProductBundle(item)) {
        bundles.push({ id: item.id, requested_quantity: item.count });
      } else {
        items.push({
          sku: (item as CartItemOffer).sku,
          requested_quantity: item.count,
        });
      }
    });

    const zoneId =
      this.orderStore.etaCalculation?.warehouse?.zone?.[
        this.checkoutStore.deliveryMethod
      ]?.id ?? '';

    const tipsAmount = this.mainStore.convertPoundsToPence(
      this.orderStore.orderTipsValue,
    );

    const destination: V3Destination =
      this.v3SelectedPupId || this.v3SelectedPupExternalId
        ? {
            kind: 'PUP',
            ...(this.v3SelectedPupId ? { pup_id: this.v3SelectedPupId } : {}),
            ...(this.v3SelectedPupExternalId
              ? { pup_external_id: this.v3SelectedPupExternalId }
              : {}),
          }
        : { kind: 'CUSTOMER' };

    const routeSelectionOrders: V3RouteSelectionItem[] = this.v3RouteSelection
      .filter(
        (sel) =>
          !!sel.template_id &&
          this.isV3RouteSelectionInCart(sel, selectedItems),
      )
      .map((sel) => ({
        warehouse_id: sel.warehouse_id,
        warehouse_code: sel.warehouse_code,
        template_id: sel.template_id,
        ...(sel.slot_delivery_details
          ? { slot_delivery_details: sel.slot_delivery_details }
          : {}),
      }));

    const addressParts = splitAddressLine(
      deliveryAddress?.address1 ?? '',
      deliveryAddress?.city,
      deliveryAddress?.addressBld,
    );

    const v3Request: V3CalculateRequest = {
      address:
        this.userStore.isAuthorized && deliveryAddress
          ? {
              latitude: deliveryAddress.coordinates.lat,
              longitude: deliveryAddress.coordinates.lng,
              ...(addressParts.street
                ? { address_1: addressParts.street }
                : {}),
              ...(deliveryAddress.city ? { city: deliveryAddress.city } : {}),
              ...(deliveryAddress.zip ? { zip: deliveryAddress.zip } : {}),
              ...(addressParts.building
                ? { building: addressParts.building }
                : {}),
            }
          : null,
      destination,
      include_pups: true,
      order: {
        currency: this.orderStore.currency.toUpperCase(),
        zone_id: zoneId,
        items,
        bundles,
        should_use_bonuses: false,
        should_use_loyalty_points: loyaltyPointsStore.useLoyaltyPoints,
        ...(this.promocode.coupon?.code
          ? { promocode: this.promocode.coupon.code }
          : {}),
        ...(tipsAmount > 0 ? { tips_amount: tipsAmount } : {}),
      },
      recipient: this.userStore.isAuthorized
        ? {
            email: this.userStore.email,
            full_name: this.userStore.fullName,
            phone: this.userStore.phone,
          }
        : null,
      ...(routeSelectionOrders.length > 0
        ? { routeSelection: { orders: routeSelectionOrders } }
        : {}),
    };

    const v3Response = await this.requestV3Calculate(v3Request);
    const blockingReason = detectV3BlockingIssue(v3Response);

    runInAction(() => {
      const previousSelectionByWh = new Map(
        this.v3RouteSelection.map((sel) => [sel.warehouse_id, sel]),
      );

      this.v3FindToken = v3Response.data.findToken;
      this.v3CalculatedOrders = v3Response.data.orders;
      this.v3AvailablePups = v3Response.data.available_pups ?? [];
      this.v3Totals = v3Response.data.totals;
      this.v3RouteSelection = v3Response.data.orders.map((o) => {
        const previous = previousSelectionByWh.get(o.warehouse_id);
        const previousStillAvailable = previous?.template_id
          ? o.available_routes.some(
              (r) => r.template_id === previous.template_id,
            )
          : false;
        const templateId = previousStillAvailable
          ? previous!.template_id
          : (o.route?.template_id ?? '');
        return {
          warehouse_id: o.warehouse_id,
          warehouse_code: o.warehouse_code,
          template_id: templateId,
          ...(previousStillAvailable && previous?.slot_delivery_details
            ? { slot_delivery_details: previous.slot_delivery_details }
            : {}),
        };
      });

      const slotRequiredButMissing = this.v3RouteSelection.some((sel) => {
        const childOrder = v3Response.data.orders.find(
          (o) => o.warehouse_id === sel.warehouse_id,
        );
        const selectedRoute = childOrder?.available_routes.find(
          (r) => r.template_id === sel.template_id,
        );
        return (
          selectedRoute?.slot_selection_required && !sel.slot_delivery_details
        );
      });
      this.v3BlockingIssueReason = slotRequiredButMissing
        ? 'SLOT_REQUIRED_NOT_SUPPORTED'
        : blockingReason === 'SLOT_REQUIRED_NOT_SUPPORTED'
          ? null
          : blockingReason;

      this.announcedCalculateFailure = null;
      const availablePups = v3Response.data.available_pups ?? [];
      if (this.v3SelectedPupId || this.v3SelectedPupExternalId) {
        // Any wms point is orderable: calculate resolves `pup_external_id` to a
        // `pup_id`, which `new` requires — sync it back from the response.
        const resolvedPupId = v3Response.data.pup_id;
        if (resolvedPupId) {
          this.v3SelectedPupId = resolvedPupId;
        }
        if (!this.v3SelectedPupExternalId) {
          const selectedPup = availablePups.find(
            (p) => p.id === this.v3SelectedPupId,
          );
          if (selectedPup) {
            this.v3SelectedPupExternalId = selectedPup.code;
          }
        }
      }
      this.syncV3PupWithDeliveryAddress(availablePups);
    });

    return adaptV3CalculateToV2(v3Response);
  }

  private processCalculateResponse(response: CartCalculationResponse): void {
    const {
      base_total: totalBasePence = 0,
      discount_total: totalDiscountPence = 0,
      paid_total: paidPence,
      promocode_discount: promocodeDiscountPence = 0,
      paid_bonuses = 0,
      paid_loyalty_points: ownLoyaltyPoints = 0,
      paid_external_bonuses = 0,
      tax_percent: taxPercent,
      tax_amount: taxAmount,
      orders,
      minimal_order_value_error,
      availableAmountForCurrentOrder = 0,
      earned_bonus_points,
      pickup_slots,
    } = response;

    if (pickup_slots) {
      this.setPickupSlotsPolicy(pickup_slots);
    }

    if (!orders[0]) {
      throw new Error('Something went wrong');
    }

    const {
      promocode,
      promocodeError,
      promocodeCalculationNote,
      customs_duties_fee,
      registration_fee,
      customs_service_fee,
    } = orders[0];

    // Converted-price companies (DubaiExpress) use the backend-converted totals
    // (company currency, incl. customs) instead of the raw base-currency values.
    const useConverted = company.convertedPricesEnabled;
    const basePence = useConverted
      ? (response.convertedTotal ?? totalBasePence)
      : totalBasePence;
    const discountPence = useConverted
      ? (response.convertedDiscountTotal ?? totalDiscountPence)
      : totalDiscountPence;
    const finalPaidPence = useConverted
      ? (response.convertedPaidTotal ?? paidPence)
      : paidPence;
    const promocodeDiscountToShowPence = useConverted
      ? (response.convertedPromocodeDiscount ?? promocodeDiscountPence)
      : promocodeDiscountPence;
    const paidWithDiscountPence = useConverted
      ? finalPaidPence
      : cartPaidWithDiscountPence({
          base_total: totalBasePence,
          discount_total: totalDiscountPence,
          promocode_discount: promocodeDiscountPence,
          paid_bonuses,
          paid_loyalty_points: ownLoyaltyPoints,
        });

    this.mainStore.sendToRN('sendTags', {
      cart_price: this.mainStore.toFloat(finalPaidPence),
    });

    const mindboxLoyaltyEnabled = userStore.mindboxLoyaltyStore.enabled;
    const paidLoyaltyPoints = mindboxLoyaltyEnabled
      ? paid_external_bonuses
      : ownLoyaltyPoints;

    const amountLeftToDiscount = this.calculateAmountLeftToDiscount(
      basePence,
      discountPence,
    );

    const serviceFeePence = orders.reduce(
      (acc, order) => acc + (order.service_fee ?? 0),
      0,
    );

    const paidWithoutDiscountPence = cartPaidWithoutDiscountPence({
      basePence,
      discountPence,
      paidBonusesPence: paid_bonuses,
    });

    this.deliveriesOrders = orders;
    this.deliveryParcels = resolveDeliveryParcels(response);
    this.cartTotalsPence = {
      base_total: totalBasePence,
      discount_total: totalDiscountPence,
      paid_total: paidPence,
    };
    this.availableAmountForCurrentOrder = availableAmountForCurrentOrder;
    this.paidExternalBonuses = paid_external_bonuses;
    this.earnedBonusPoints =
      earned_bonus_points ??
      orders.reduce(
        (acc, order) => acc + (order.mindbox_bonus_points?.earned ?? 0),
        0,
      );
    this.totalCartPrice = {
      amountLeftToDiscount,
      base: this.mainStore.convertPenceToPounds(basePence),
      discount: this.mainStore.convertPenceToPounds(discountPence),
      paidWithDiscount: this.mainStore.convertPenceToPounds(
        paidWithDiscountPence,
      ),
      paidWithoutDiscount: this.mainStore.convertPenceToPounds(
        paidWithoutDiscountPence,
      ),
      paidWithoutDiscountPence,
      discountPence,
      paid: this.mainStore.convertPenceToPounds(
        mindboxLoyaltyEnabled
          ? finalPaidPence
          : finalPaidPence - ownLoyaltyPoints,
      ),
      promocodeDiscount: this.mainStore.convertPenceToPounds(
        promocodeDiscountToShowPence,
      ),
      loyaltyPointsDiscount:
        this.mainStore.convertPenceToPounds(paidLoyaltyPoints),
      paidBonusesPence: paid_bonuses,
      paidLoyaltyPointsPence: paidLoyaltyPoints,
      taxPercent,
      taxAmount,
      serviceFee: serviceFeePence,
      minimal_order_value_error,
      customsDutiesFee: response.customsDutiesFeeTotal ?? customs_duties_fee,
      registrationFee: response.registrationFee ?? registration_fee,
      customsServiceFee:
        response.customsDutiesServiceFee ??
        response.customsDutiesFee ??
        customs_service_fee,
      deliveryGroupPrice: resolveDeliveryGroupPrice(response, useConverted),
    };

    if (this.cart.length) {
      const slugsList = this.cart.reduce<Record<string, string>>(
        (acc, item) => {
          if (!item.slug) {
            return acc;
          }
          acc[item.sku] = item.slug;
          return acc;
        },
        {},
      );

      this.updateCartItems(
        this.selectedCartItems
          .filter((item) => !isProductBundle(item))
          .map((item) => item.sku),
        this.buildCartObj(),
        orders
          .flatMap(({ items }) => items)
          .map((item) => {
            item.slug = slugsList[item.sku];
            return item;
          }),
      );
    }

    if (promocode) {
      this.promotionStore.handleCalculateWithPromocodeResponse(
        promocodeError,
        promocodeCalculationNote,
      );
    } else if (this.promotionStore.promocode.coupon) {
      // A promocode was applied (coupon set) but the backend echoed
      // `promocode: null` — it applied to nothing (e.g. cart left only with
      // discounted / promo-excluded items). Without this branch the code stays
      // in the neutral post-validate state with no message. Reuse the standard
      // error path so the user sees the same outcome as the echoed
      // PROMOCODE_NO_ITEM_APPLIED response.
      this.promotionStore.handleCalculateWithPromocodeResponse(
        PROMOCODE_ERROR_CODE.NO_ITEM_APPLIED,
        null,
      );
    } else if (this.promotionStore.applyPromocodeMeta.isLoading) {
      this.promotionStore.applyPromocodeMeta.setLoading(false);
    }
  }

  /**
   * LMS calculate failure shape: `{errors: ["LMS V3 calculation failed"], data: {issues: [...]}}`.
   * Business outcomes (stock changes, age restriction) arrive in other shapes and
   * are handled by their own branches.
   */
  private announceCalculateFailure(error: unknown): void {
    if (!(error instanceof AxiosError)) {
      return;
    }

    const status = error.response?.status;
    const data = error.response?.data as V3CalculateErrorBody | undefined;
    const issues = data?.data?.issues ?? [];
    const isLmsFailure =
      company.hasNewLogistics &&
      ((data?.errors?.length ?? 0) > 0 || issues.length > 0);
    // Network failures are already announced by requestWrapper.
    const isServerFailure = status !== undefined && status >= 500;

    if (!isLmsFailure && !isServerFailure) {
      return;
    }

    const reason = isLmsFailure
      ? (issues[0]?.code ?? data?.data?.error_code ?? 'LMS_CALCULATE_FAILED')
      : 'CALCULATE_SERVER_ERROR';
    // A rejected point must not be restored on the next checkout visit.
    if (
      reason === 'PICKUP_POINT_NOT_FOUND' ||
      reason === 'INVALID_PUP_SELECTION'
    ) {
      this.v3LastPupSelection = null;
    }
    // Keyed by the rejected pickup point too, so switching to another bad one
    // is announced again instead of failing silently.
    const announcement = `${reason}:${data?.data?.pup_id ?? data?.data?.pup_external_id ?? ''}`;
    if (this.announcedCalculateFailure === announcement) {
      return;
    }
    this.announcedCalculateFailure = announcement;

    this.mainStore.pushAlert(
      'error',
      i18n.t(V3_CALCULATE_FAILURE_MESSAGES[reason] ?? 'errors:calculateFailed'),
    );
  }

  // `ITEM_UNAVAILABLE` reuses the stock-changed modal: confirming it drops the
  // rejected items from the cart, which re-runs calculate without them.
  private openStockModalForV3Issues(error: unknown): boolean {
    if (!company.hasNewLogistics || !(error instanceof AxiosError)) {
      return false;
    }

    const body = error.response?.data as V3CalculateErrorBody | undefined;
    const cartObj = this.buildCartObj();
    const items: CalculateStockOfferError[] = [];
    const bundles: Record<number, CalculateStockBundleError> = {};

    for (const issue of body?.data?.issues ?? []) {
      if (issue.code === 'BUNDLE_UNAVAILABLE') {
        const bundleId = issue.bundle_id ?? issue.payload?.bundle_id;
        const cartItem =
          bundleId !== undefined ? cartObj[String(bundleId)] : undefined;
        if (!cartItem) {
          continue;
        }
        items.push({
          sku: '',
          bundle_id: String(bundleId),
          on_stock: 0,
          requested_quantity: cartItem.count,
          warehouse_code: '',
        });
        bundles[Number(bundleId)] = { sellable: 0 };
        continue;
      }

      const cartItem = issue.sku ? cartObj[issue.sku] : undefined;
      if (issue.code !== 'ITEM_UNAVAILABLE' || !issue.sku || !cartItem) {
        continue;
      }
      items.push({
        sku: issue.sku,
        bundle_id: '',
        on_stock: 0,
        requested_quantity: cartItem.count,
        warehouse_code: '',
      });
    }

    if (!items.length) {
      return false;
    }

    this.cartStockChangesStore.openNotifierModal({
      items,
      bundles: Object.keys(bundles).length ? bundles : null,
    });
    return true;
  }

  setPickupSlotsPolicy(policy: PickupSlotsPolicy): void {
    const next = {
      enabled: Boolean(policy.enabled),
      required: Boolean(policy.enabled && policy.required),
    };

    this.isPickupSlotsPolicyKnown = true;

    if (
      next.enabled === this.pickupSlotsPolicy.enabled &&
      next.required === this.pickupSlotsPolicy.required
    ) {
      return;
    }

    this.pickupSlotsPolicy = next;
  }

  /** Forgets the verdict: another pickup point (or method) has to be priced first. */
  resetPickupSlotsPolicy(): void {
    this.isPickupSlotsPolicyKnown = false;
    this.pickupSlotsPolicy = { enabled: false, required: false };
  }

  /**
   * The backend refused the pickup-slot choice (feature toggled per company, so the
   * cart may be ahead of or behind the policy). Adopt the policy the error implies and
   * let the checkout rebuild its deliveries; the recalculation follows from the
   * changed slot selection.
   */
  private handlePickupSlotRejection(code: string): boolean {
    if (code === 'PICKUP_SLOTS_DISABLED') {
      this.setPickupSlotsPolicy({ enabled: false, required: false });
      this.pickupSlotsPolicyVerdict += 1;

      return true;
    }

    if (code === 'PICKUP_SLOT_REQUIRED') {
      this.setPickupSlotsPolicy({ enabled: true, required: true });
      this.pickupSlotsPolicyVerdict += 1;

      return true;
    }

    return false;
  }

  private announcePickupPointRejection(code: string): boolean {
    if (!isPickupPointErrorCode(code)) {
      return false;
    }

    const announcement = `${code}:${this.userStore.deliveryAddress?.partnerPupExternalId ?? ''}`;

    if (this.announcedCalculateFailure !== announcement) {
      this.announcedCalculateFailure = announcement;
      this.mainStore.pushAlert(
        'error',
        i18n.t(`orderErrors:${code}_description`),
      );
    }

    return true;
  }

  /** A refused calculate leaves an in-flight promocode application hanging. */
  private resetPendingPromocode(): void {
    if (!this.promotionStore.applyPromocodeMeta.isLoading) {
      return;
    }

    this.promocode.success = false;
    this.promocode.errorType = 'error';
    this.promocode.message = i18n.t('errors:promocodeError');
    this.promotionStore.applyPromocodeMeta.setLoading(false);
  }

  private handleCalculateError(
    error: unknown,
    request: CartCalculationRequest,
  ): void {
    if (!this.openStockModalForV3Issues(error)) {
      this.announceCalculateFailure(error);
    }

    const cartObj = this.buildCartObj();

    if (error instanceof AxiosError) {
      const payload = error.response?.data?.data;
      // `error_code` is the established field (MISSING_BIRTHDAY, AGE_RESTRICTION);
      // `code` is what the pickup-slot rejections were specced with. Accept both.
      const code = payload?.error_code ?? payload?.code;

      if (
        typeof code === 'string' &&
        this.handlePickupSlotRejection(
          pickupRejectionCode({
            code,
            sentAsapPickup:
              request.delivery_method === DeliveryMethods.CLICK_AND_COLLECT &&
              !request.deliveries.some(
                (delivery) => delivery.slot_delivery_details,
              ),
            isSamePickupCart:
              this.checkoutStore.isPickupSelected &&
              request.warehouse_code ===
                this.orderStore.etaCalculation?.warehouse.code,
            bookableWindowsCount: this.checkoutStore.bookablePickupSlots.length,
          }),
        )
      ) {
        this.resetPendingPromocode();
        this.reportCalculateRejection(error);

        return;
      }

      if (typeof code === 'string' && this.announcePickupPointRejection(code)) {
        this.resetPendingPromocode();
        this.reportCalculateRejection(error);

        return;
      }
    }

    if (error instanceof AxiosError && error.response?.data?.data?.length) {
      this.changeCountProductList = [];
      for (let i = 0; i < error.response.data.data.length; i++) {
        if (error.response.data.data[i].on_stock <= 0) {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          cartObj[error.response.data.data[i].sku].count = 0;
        } else {
          // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
          cartObj[error.response.data.data[i].sku].count =
            error.response.data.data[i].on_stock;
          this.changeCountProductList.push(
            // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
            toJS(cartObj[error.response.data.data[i].sku]),
          );
        }
      }
      this.cart = Object.values(cartObj);
    }

    if (
      error instanceof AxiosError &&
      error?.response?.data?.data?.error_code === 'MISSING_BIRTHDAY'
    ) {
      this.isMissingBirthdayErrorCatched = true;
    }

    if (
      error instanceof AxiosError &&
      error?.response?.data?.data?.data?.items?.length
    ) {
      const { response } = error;
      const errorResponse: CalculateStockErrorInstance = {
        isAgeRestriction:
          response?.data?.data?.error_code === 'AGE_RESTRICTION',
        items: [],
        bundles: null,
        ...response?.data?.data?.data,
      };

      this.cartStockChangesStore.openNotifierModal(errorResponse);
      this.changeCountProductList.push(
        ...(errorResponse.items
          .map((item) => cartObj[item.sku])
          .filter(Boolean) as CartItem[]),
      );
    }

    this.resetPendingPromocode();

    if (
      error instanceof AxiosError &&
      error.response?.status === 500 &&
      error.response?.data?.errors?.includes('Calculate Cart Api Error')
    ) {
      this.orderStore.reCalculateDeliveries();
    }

    this.reportCalculateRejection(error);
  }

  /**
   * Reports a calculation the backend refused on business grounds.
   *
   * These arrive as 4xx, which the API layer files as a breadcrumb — right for
   * an ordinary failed request, wrong for this one. Until the customer resolves
   * it the cart cannot be priced and no order can be placed, which makes it the
   * last step of a great many orders that never happen: the stock shortfall
   * alone accounts for tens of thousands of occurrences a month, and none of
   * them can currently be counted.
   *
   * Grouped by the backend's own code so each cohort stays separable, and
   * `once` per code because the cart re-calculates on a poll — one customer is
   * one occurrence, not one per retry.
   */
  private reportCalculateRejection(error: unknown): void {
    if (!(error instanceof AxiosError)) {
      return;
    }

    const status = error.response?.status;
    if (status === undefined || status >= 500) {
      return;
    }

    const body = error.response?.data;
    const payload = body?.data;
    const issues: V3Issue[] = Array.isArray(payload?.issues)
      ? payload.issues
      : [];
    const etaErrorCode: string | null = Array.isArray(payload)
      ? null
      : (payload?.eta_error_code ?? null);
    const errorCode: string | null = Array.isArray(payload)
      ? 'STOCK_SHORTFALL'
      : (issues[0]?.code ??
        payload?.error_code ??
        payload?.code ??
        etaErrorCode ??
        (body?.errors?.length ? 'LMS_CALCULATE_FAILED' : null));

    if (!errorCode) {
      return;
    }

    reportWarning(DOMAIN.cart, 'Calculate rejected', {
      reason: REASON.cart.calculateRejected,
      once: true,
      fingerprint: [errorCode],
      extras: {
        errorCode,
        etaErrorCode,
        status,
        itemsAffected: Array.isArray(payload)
          ? payload.length
          : issues.length || (payload?.data?.items?.length ?? null),
        skus: issues.map((issue) => issue.sku).filter(Boolean),
        cartItemsCount: this.cart.length,
      },
    });
  }

  private buildCartObj(): Record<string, CartItem> {
    const cartObj: Record<string, CartItem> = {};
    this.selectedCartItems.forEach((item) => {
      const key = isProductBundle(item) ? `${item.id}` : item.sku;
      cartObj[key] = toJS(item);
    });
    return cartObj;
  }

  private calculateAmountLeftToDiscount(
    baseTotalPence: number,
    discountTotalPence: number,
  ): string {
    const totalDiscountPence = baseTotalPence - discountTotalPence;

    if (!this.promocode.success || !this.promocode.coupon) {
      return '0';
    }

    const {
      type,
      minimumPurchase,
      value: discountValuePence,
    } = this.promocode.coupon;

    if (totalDiscountPence < minimumPurchase) {
      const result = minimumPurchase - totalDiscountPence;
      return result < 0 ? '0' : this.mainStore.convertPenceToPounds(result);
    }

    if (type === 'FIXED' && totalDiscountPence < discountValuePence) {
      const result = discountValuePence - totalDiscountPence;
      return result < 0 ? '0' : this.mainStore.convertPenceToPounds(result);
    }

    return '0';
  }

  private updateCartItems(
    skuList: string[],
    cartObj: Record<string, CartItem>,
    items: CartCalculationItemResponse[],
  ): boolean {
    this.changePriceProductList = [];
    this.changeCountProductList = [];

    items.forEach((item) => {
      if (item.is_gift) {
        return;
      }
      const cartItem = cartObj[item.sku];
      if (!cartItem) {
        return;
      }

      const originCartItem = toJS(cartItem);
      let isPriceChanged = false;

      skuList = skuList.filter((sku) => sku !== item.sku);

      if (item.sellable !== undefined) {
        cartItem.sellable = item.sellable;

        const warehouseCode = this.orderStore.etaCalculation?.warehouse.code;
        if (warehouseCode) {
          (cartItem as Mutable<CartItem>).sellableWarehouses = {
            ...cartItem.sellableWarehouses,
            [warehouseCode]: String(item.sellable),
          };
        }
      }

      const prices = calculatedItemPricesPence(
        item,
        company.convertedPricesEnabled,
      );
      const itemBasePrice = prices.price;

      if (
        item.discount_amount &&
        cartItem.discountPrice !== prices.discountPrice
      ) {
        const oldDiscountPrice = cartItem.discountPrice;
        cartItem.discountPrice = prices.discountPrice;
        if (prices.discountPrice > oldDiscountPrice) {
          isPriceChanged = true;
        }
      }

      if (!item.discount_amount && cartItem.discountPrice) {
        cartItem.discountPrice = 0;
        isPriceChanged = true;
      }

      cartItem.discountAmount = prices.discountAmount;
      cartItem.membershipDiscountAmount = prices.membershipDiscountAmount;

      if (cartItem.price !== itemBasePrice) {
        const oldPrice = cartItem.price;
        cartItem.price = itemBasePrice;
        if (itemBasePrice > oldPrice) {
          isPriceChanged = true;
        }
      }

      if (isPriceChanged) {
        this.changePriceProductList.push(originCartItem);
      }
    });

    skuList.forEach((sku) => {
      const cartItem = cartObj[sku];
      if (!cartItem) {
        return;
      }
      cartItem.count = 0;
    });

    const cartItems = Object.values(cartObj);
    if (this.cart.length === cartItems.length) {
      this.cart = cartItems.map(calcProductOfferFields) as CartItem[];
    } else {
      this.cart = this.cart.map((cartItem) => {
        const updatedItem = cartItems.find(({ sku }) => sku === cartItem.sku);
        return updatedItem ?? cartItem;
      });
    }

    return !!(
      this.changePriceProductList.length || this.changeCountProductList.length
    );
  }
}
